import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Dropzone } from '@/components/ui/dropzone';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useConsultantFileService,
  useConsultantUpsertSummaryService,
} from '@/services/consultant';
import { MarkdownText } from '@/shared/MarkdownText/MarkdownText';
import type { ConsultantUpdateItem } from '@ditto/shared';
import dayjs from 'dayjs';
import { ArrowRight, ExternalLink, FileText, Trash2 } from 'lucide-react';
import { useCallback, useState, type FC } from 'react';
import { toast } from 'sonner';
import { useConsultantContext } from '../ConsultantLayout';

export const EditSummaryModal: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  update: ConsultantUpdateItem;
}> = ({ open, onOpenChange, update }) => {
  const { orgId, orgName } = useConsultantContext();
  const [shortSummary, setShortSummary] = useState(
    update.consultantShortSummary ?? ''
  );
  const [summary, setSummary] = useState(update.consultantSummary ?? '');
  const { upsertSummary, isPending } = useConsultantUpsertSummaryService();
  const { uploadFile, deleteFile, isUploading, isDeleting } =
    useConsultantFileService();
  const [currentFile, setCurrentFile] = useState(update.consultantFile);

  const handleSave = useCallback(() => {
    upsertSummary(
      {
        orgId,
        updateId: update.id,
        body: {
          shortSummary: shortSummary.trim() || null,
          summary: summary.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.success('Summary saved');
          onOpenChange(false);
        },
        onError: (e: Error) => toast.error(e.message),
      }
    );
  }, [update.id, shortSummary, summary, upsertSummary, onOpenChange]);

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (!file) return;
      uploadFile(
        { orgId, updateId: update.id, file },
        {
          onSuccess: data => {
            setCurrentFile({ id: data.fileId, name: data.fileName });
            toast.success('File uploaded');
          },
          onError: (err: Error) => toast.error(err.message),
        }
      );
    },
    [orgId, update.id, uploadFile]
  );

  const handleFileDelete = useCallback(() => {
    deleteFile(
      { orgId, updateId: update.id },
      {
        onSuccess: () => {
          setCurrentFile(null);
          toast.success('File removed');
        },
        onError: (err: Error) => toast.error(err.message),
      }
    );
  }, [orgId, update.id, deleteFile]);

  const title = update.reference?.title ?? update.title ?? 'Update';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1400px] w-[90vw] max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b">
          <div className="flex items-center gap-2">
            <DialogTitle className="text-base">{title}</DialogTitle>
            {update.materialUrl && (
              <a
                href={update.materialUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 shrink-0"
              >
                <ExternalLink className="size-3.5" />
                Link
              </a>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {dayjs(update.publicationDate).format('D MMMM YYYY')} ·{' '}
            {update.source?.title ?? ''}
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto flex gap-0 min-h-0">
          {/* Left: Qalico summary (read-only) */}
          <div className="flex-1 border-r p-5 space-y-3 overflow-y-auto">
            <Label className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">
              Qalico Analysis
            </Label>
            <div>
              <span className="text-xs text-neutral-400">Short summary</span>
              <p className="text-sm text-neutral-700 mt-0.5">
                {update.shortSummary || (
                  <span className="italic text-neutral-400">None</span>
                )}
              </p>
            </div>
            <div>
              <span className="text-xs text-neutral-400">Full analysis</span>
              <div className="mt-1 prose prose-sm prose-neutral max-w-none text-sm">
                <MarkdownText size="sm">{update.summary || ''}</MarkdownText>
              </div>
              {!update.summary && (
                <p className="text-sm italic text-neutral-400">None</p>
              )}
            </div>
          </div>

          {/* Right: Consultant summary (editable) */}
          <div className="flex-1 p-5 space-y-3 overflow-y-auto">
            <Label className="text-xs font-semibold text-green-700 uppercase tracking-wider">
              {orgName} Analysis
            </Label>
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label className="text-xs text-neutral-500">
                  Short summary
                </Label>
                {update.shortSummary && (
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() =>
                          setShortSummary(update.shortSummary ?? '')
                        }
                        className="text-neutral-400 hover:text-blue-600 transition-colors"
                      >
                        <ArrowRight className="size-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p className="text-xs">Copy Qalico short summary</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
              <Input
                value={shortSummary}
                onChange={e => setShortSummary(e.target.value)}
                placeholder="1 sentence summary"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label className="text-xs text-neutral-500">
                  Full analysis
                </Label>
                {update.summary && (
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => setSummary(update.summary ?? '')}
                        className="text-neutral-400 hover:text-blue-600 transition-colors"
                      >
                        <ArrowRight className="size-3" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p className="text-xs">Copy Qalico analysis</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
              <Textarea
                value={summary}
                onChange={e => setSummary(e.target.value)}
                placeholder="Write a detailed summary..."
                className="min-h-[250px]"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-neutral-500">
                Analysis document
              </Label>
              {currentFile ? (
                <div className="flex items-center gap-2 rounded-md border px-3 py-2.5 text-sm bg-white">
                  <FileText className="size-4 text-blue-600 shrink-0" />
                  <span className="truncate flex-1 text-neutral-700">
                    {currentFile.name}
                  </span>
                  <button
                    type="button"
                    onClick={handleFileDelete}
                    disabled={isDeleting}
                    className="text-neutral-400 hover:text-red-600 transition-colors"
                    title="Remove file"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              ) : (
                <Dropzone
                  onFilesSelected={handleFilesSelected}
                  isLoading={isUploading}
                  title="Drop your analysis document here"
                  subtitle="PDF, Word, Excel, or PowerPoint"
                  buttonText="Browse files"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                  multiple={false}
                  className="!min-h-[120px] !p-4"
                />
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 px-6 py-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
